import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'

import { ActiveFiltersMap } from 'clickNgo/components/dataFilterBar'
import { ProfilingCodeType } from 'clickNgo/entities/profilingCode'
import { ICrudFetchOptions } from 'clickNgo/models/crud/utils'
import { IForm } from 'core-components/classes/models/IForm'
import { IDataProviderFilter, IFilter, Logic, Operator } from 'core-components/types'
import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'

type Params = {
  searchQuery?: string
  individualFilters?: ActiveFiltersMap
} & (
  | {
      partnerId: string
      topPartnerId?: never
    }
  | {
      topPartnerId: string
      partnerId?: never
    }
)

export function exportContacts(
  { partnerId, topPartnerId, searchQuery = '', individualFilters }: Params,
  options?: ICrudFetchOptions
) {
  const source = CrudStore.get<IForm>(CrudStoreItem.PartnerContacts, options?.cacheKey || partnerId)
  const partnerIdToUse = partnerId || topPartnerId!
  const partnerIdField = partnerId ? 'PartnerID' : 'TopPartnerID'

  const filterByTopPartnerId = {
    field: partnerIdField,
    operator: Operator.EQUAL,
    value: partnerIdToUse,
  }

  const filtersBySearchQuery = {
    logic: Logic.OR,
    filters: [
      {
        field: 'ContactID',
        operator: Operator.CONTAINS,
        value: searchQuery,
      },
      {
        field: 'LearnerID',
        operator: Operator.CONTAINS,
        value: searchQuery,
      },
      {
        field: 'FirstName',
        operator: Operator.CONTAINS,
        value: searchQuery,
      },
      {
        field: 'LastName',
        operator: Operator.CONTAINS,
        value: searchQuery,
      },
      {
        field: 'Email',
        operator: Operator.CONTAINS,
        value: searchQuery,
      },
      {
        field: 'PR_HP_ROLE',
        operator: Operator.CONTAINS,
        value: searchQuery,
      },
      {
        field: 'Status',
        operator: Operator.CONTAINS,
        value: searchQuery,
      },
      {
        field: 'WorkPhone',
        operator: Operator.CONTAINS,
        value: searchQuery,
      },
      {
        field: 'MobilePhone',
        operator: Operator.CONTAINS,
        value: searchQuery,
      },
      {
        field: 'LocationID',
        operator: Operator.CONTAINS,
        value: searchQuery,
      },
      {
        field: 'PartnerID',
        operator: Operator.CONTAINS,
        value: searchQuery,
      },
      {
        field: 'CommunicationName',
        operator: Operator.CONTAINS,
        value: searchQuery,
      },
      {
        field: 'City',
        operator: Operator.CONTAINS,
        value: searchQuery,
      },
    ],
  }

  const getFilterStringValue = (value: unknown): string => {
    if (value === null || value === undefined) return ''
    return String(value)
  }

  const filtersByIndividualFilters = {
    logic: Logic.AND,
    filters: [
      {
        field: 'ContactID',
        operator: Operator.CONTAINS,
        value: getFilterStringValue(individualFilters?.contactId),
      },
      {
        field: 'FirstName',
        operator: Operator.CONTAINS,
        value: getFilterStringValue(individualFilters?.firstName),
      },
      {
        field: 'LastName',
        operator: Operator.CONTAINS,
        value: getFilterStringValue(individualFilters?.lastName),
      },
      {
        field: 'Email',
        operator: Operator.CONTAINS,
        value: getFilterStringValue(individualFilters?.email),
      },
      {
        field: 'PR_HP_ROLE',
        operator: Operator.CONTAINS,
        value: getFilterStringValue(individualFilters?.hpRoles),
      },
      {
        field: 'MobilePhone',
        operator: Operator.CONTAINS,
        value: getFilterStringValue(individualFilters?.mobilePhoneNumber),
      },
      {
        field: 'WorkPhone',
        operator: Operator.CONTAINS,
        value: getFilterStringValue(individualFilters?.workPhoneNumber),
      },
      {
        field: 'PartnerID',
        operator: Operator.CONTAINS,
        value: getFilterStringValue(individualFilters?.partnerId),
      },
      {
        field: 'CommunicationName',
        operator: Operator.CONTAINS,
        value: getFilterStringValue(individualFilters?.communicationName),
      },
      {
        field: 'City',
        operator: Operator.CONTAINS,
        value: getFilterStringValue(individualFilters?.city),
      },
      {
        field: 'UserRights',
        operator: Operator.CONTAINS,
        value: getFilterStringValue(individualFilters?.[ProfilingCodeType.UserRights]),
      },
      {
        field: 'Tools',
        operator: Operator.CONTAINS,
        value: getFilterStringValue(individualFilters?.[ProfilingCodeType.Tools]),
      },
      {
        field: 'Campaigns',
        operator: Operator.CONTAINS,
        value: getFilterStringValue(individualFilters?.[ProfilingCodeType.Campaigns]),
      },
    ].filter((i) => Boolean(i.value)),
  }

  const initialFilters: Array<IDataProviderFilter | IFilter> = [filterByTopPartnerId]

  if (searchQuery) {
    initialFilters.push(filtersBySearchQuery)
  }

  if (filtersByIndividualFilters.filters.some((el) => Boolean(el.value))) {
    initialFilters.push(filtersByIndividualFilters)
  }

  source.setFilter({
    logic: Logic.AND,
    filters: initialFilters,
  })

  source.setDefaultFilter({
    logic: Logic.AND,
    filters: [
      {
        field: partnerIdField,
        operator: Operator.EQUAL,
        value: partnerIdToUse,
      },
    ],
  })

  return source.export(`contacts_of_${partnerIdToUse}`).catch((error) => {
    Logger.error(error)
    return null
  })
}
